/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 💬 Stale-While-Revalidate localStorage Cache
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// Tiny wrapper around localStorage that gives every consumer a consistent
/// cache shape and read/write helpers. The dashboard layer is responsible for
/// the actual SWR control flow (paint cached → background fetch → swap) — this
/// composable only provides the storage primitive.
///
/// Cache entry shape:
///   {
///     data: T,                 // the resolved value
///     ts: number,              // ms since epoch when cached
///     sfId: string | null,     // Snowflake account this was fetched against
///     version: number,         // bump COST_CACHE_VERSION to invalidate every entry
///   }
///
/// Keys are always namespaced as `pulsar:<feature>:<scope>` so we never collide
/// with anything else in localStorage. The Snowflake account id (sfId) is part
/// of the *value* rather than the key — when an entry's sfId doesn't match the
/// current session, we ignore it (and overwrite).

export const COST_CACHE_VERSION = 1
/// Default freshness window. ACCOUNT_USAGE views typically lag 45 min – 3 hr,
/// so cached data younger than this is essentially indistinguishable from a
/// fresh query in terms of underlying Snowflake state.
export const DEFAULT_TTL_MS = 30 * 60 * 1000

interface CacheEntry<T> {
  data: T
  ts: number
  sfId: string | null
  version: number
}

interface ReadResult<T> {
  data: T
  ageMs: number
  isFresh: boolean
}

export function useSwrCache() {
  // Server-side rendering safety — there's no localStorage on the server.
  // All getters degrade to misses (null), and setters become no-ops.
  const isClient = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'

  /**
   * Build a namespaced key. Never embed `sfId` in the key; we write it into
   * the entry value so we can detect account swaps and invalidate.
   */
  function buildKey(feature: string, scope: string = 'default'): string {
    return `pulsar:${feature}:${scope}`
  }

  /**
   * Return a typed cache entry if one exists and matches the current session.
   * Stale entries are returned (caller decides whether to refetch); only
   * entries from a different sfId or older cache schema are treated as misses.
   */
  function get<T>(key: string, opts: { sfId?: string | null; ttlMs?: number } = {}): ReadResult<T> | null {
    if (!isClient) return null
    try {
      const raw = window.localStorage.getItem(key)
      if (!raw) return null
      const entry = JSON.parse(raw) as CacheEntry<T>
      if (!entry || typeof entry !== 'object') return null
      if (entry.version !== COST_CACHE_VERSION) return null
      // Account boundary — cached data is invalid if it was fetched against a
      // different Snowflake account than the current session.
      if (opts.sfId !== undefined && entry.sfId && entry.sfId !== opts.sfId) return null

      const ageMs = Date.now() - entry.ts
      const ttl = opts.ttlMs ?? DEFAULT_TTL_MS
      return { data: entry.data, ageMs, isFresh: ageMs < ttl }
    } catch (err) {
      console.warn('[useSwrCache] failed to read', key, err)
      return null
    }
  }

  function set<T>(key: string, data: T, opts: { sfId?: string | null } = {}): void {
    if (!isClient) return
    try {
      const entry: CacheEntry<T> = {
        data,
        ts: Date.now(),
        sfId: opts.sfId ?? null,
        version: COST_CACHE_VERSION,
      }
      window.localStorage.setItem(key, JSON.stringify(entry))
    } catch (err) {
      // localStorage can throw on quota exceeded; degrade silently.
      console.warn('[useSwrCache] failed to write', key, err)
    }
  }

  function remove(key: string): void {
    if (!isClient) return
    try { window.localStorage.removeItem(key) } catch { /* noop */ }
  }

  /**
   * Clear every Pulsar cache entry. Useful on logout or account switch.
   * Iterates the prefix so we don't accidentally wipe other keys.
   */
  function clearAll(prefix: string = 'pulsar:'): void {
    if (!isClient) return
    try {
      const toDelete: string[] = []
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i)
        if (k && k.startsWith(prefix)) toDelete.push(k)
      }
      toDelete.forEach(k => window.localStorage.removeItem(k))
    } catch (err) {
      console.warn('[useSwrCache] clearAll failed', err)
    }
  }

  return { buildKey, get, set, remove, clearAll }
}
